import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTimeOffTypes, useCreateTimeOffType, useUpdateTimeOffType, useDeleteTimeOffType } from '../../hooks/useTimeOff';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';
import TimeOffTypeForm, {
  DEFAULT_TIME_OFF_TYPE_FORM,
  describeBehaviour,
  formToInput,
  typeToForm,
  type TimeOffTypeFormValues,
} from '../../components/admin/TimeOffTypeForm';

/**
 * MKS-327 — admin page for time-off types.
 *
 * Behind the flag this is a behaviour editor: every attribute the rest of the
 * app reads (category, paid, counts toward the hours target, allowance, needs
 * approval, partial day, colour, Slack status, archived) is set here, because
 * no code path is allowed to infer any of it from the type's name or code any
 * more.
 */
export default function TimeOffTypePage() {
  const navigate = useNavigate();
  // Archived types have to be listed here — this is the only place they can be
  // restored from. Every picker gets the default (pickable) list.
  const { data: types, isLoading } = useTimeOffTypes({ includeArchived: true });
  const createMutation = useCreateTimeOffType();
  const updateMutation = useUpdateTimeOffType();
  const deleteMutation = useDeleteTimeOffType();

  const [showForm, setShowForm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [newType, setNewType] = useState<TimeOffTypeFormValues>(DEFAULT_TIME_OFF_TYPE_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingType, setEditingType] = useState<TimeOffTypeFormValues>(DEFAULT_TIME_OFF_TYPE_FORM);

  const handleCreate = async (values: TimeOffTypeFormValues) => {
    setFormError(null);
    try {
      await createMutation.mutateAsync(formToInput(values));
      setShowForm(false);
      setNewType(DEFAULT_TIME_OFF_TYPE_FORM);
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const handleUpdate = async (values: TimeOffTypeFormValues) => {
    if (editingId === null) return;
    setFormError(null);
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        input: formToInput(values),
      });
      setEditingId(null);
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(deleteId);
      setDeleteId(null);
    } catch (err) {
      setDeleteError((err as Error).message);
    }
  };

  /**
   * Archive / restore is a one-click toggle rather than a trip through the
   * editor: it is the action an admin reaches for when a type falls out of
   * use, and the delete button next to it only works for a type no request
   * has ever used.
   */
  const toggleArchived = async (id: number, archived: boolean) => {
    setFormError(null);
    try {
      await updateMutation.mutateAsync({ id, input: { archived } });
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const startEditing = (type: NonNullable<typeof types>[number]) => {
    setEditingId(type.id);
    setEditingType(typeToForm(type));
    setFormError(null);
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/admin')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Admin
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Time Off Types</h1>
          <Button size="sm" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : 'Add Type'}
          </Button>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Each type&rsquo;s behaviour is configured here — the rest of the app reads these
          settings, never the type&rsquo;s name.
        </p>
      </div>

      {showForm && (
        <TimeOffTypeForm
          values={newType}
          onChange={setNewType}
          onSubmit={handleCreate}
          onCancel={() => { setShowForm(false); setFormError(null); }}
          submitLabel="Create"
          isLoading={createMutation.isPending}
          error={formError}
          showBehaviour
        />
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Behaviour
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {types?.map((type) => (
              <tr key={type.id} className={type.archived ? 'bg-gray-50' : 'hover:bg-gray-50'}>
                {editingId === type.id ? (
                  <td colSpan={3} className="px-6 py-3">
                    <TimeOffTypeForm
                      values={editingType}
                      onChange={setEditingType}
                      onSubmit={handleUpdate}
                      onCancel={() => { setEditingId(null); setFormError(null); }}
                      submitLabel="Save"
                      isLoading={updateMutation.isPending}
                      error={formError}
                      showBehaviour
                    />
                  </td>
                ) : (
                  <>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">
                      <span className="inline-flex items-center gap-2">
                        <span
                          aria-hidden="true"
                          className="inline-block h-3 w-3 rounded-full border border-black/10"
                          style={{ backgroundColor: type.colour ?? '#6366F1' }}
                        />
                        {type.name || '-'}
                        {type.archived && (
                          <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                            Archived
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">{describeBehaviour(type)}</td>
                    <td className="px-6 py-3 text-right space-x-3">
                      <button
                        onClick={() => startEditing(type)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => toggleArchived(type.id, !type.archived)}
                        className="text-amber-600 hover:text-amber-800 text-sm font-medium"
                      >
                        {type.archived ? 'Restore' : 'Archive'}
                      </button>
                      <button
                        onClick={() => { setDeleteId(type.id); setDeleteError(null); }}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Time Off Type"
        message="Deleting only works for a type no request has ever used. Archive it instead to keep the history and stop people picking it."

        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
        error={deleteError}
      />
    </div>
  );
}
